import {createSelector} from '@reduxjs/toolkit'

import {isNil} from '@shared/helpers/lang'

import {FamilyUser} from '@web/core/constants'

import {RootState} from './interfaces'

export const payNextSelector = (state: RootState) => state.payNext

/** 무통장 입금 정보 selector */
export const virtualAccountInfoSelector = createSelector(
    (state: RootState) => state.payNext.virtualAccount,
    ({bankCodes, selected}) => {
        return {
            /** 무통장 입금 계좌 목록 */
            bankCodes,
            /** 선택한 무통장 입금 은행 code */
            selectedCode: selected.code,
        }
    },
)

/** 환불 계좌 정보 selector */
export const refundInfoSelector = createSelector(
    (state: RootState) => state.payNext.refund,
    ({method, accounts, selected}) => {
        const defaultRefundAccount = accounts.find(({accountKey}) => accountKey === selected.accountKey) ?? null
        return {
            /** 대표 환불 계좌 정보 */
            defaultRefundAccount,
            /** 환불방법 */
            refundMethod: method,
            /** 구매자 환불 계좌 목록 */
            refundAccounts: accounts.map((account) => ({
                ...account,
                defaultRefundAccount: account.accountKey === selected.accountKey,
            })),
            selected: {
                /** 선택된 환불 계좌 key */
                key: selected.accountKey,
                /** 선택된 환불 계좌 info */
                info: defaultRefundAccount,
            },
        }
    },
)

export const familyPaySelector = (state: RootState) => state.payNext.familyPay

export const familyPayMemberPaymentInfoSelector = createSelector(payNextSelector, ({familyPay}) =>
    familyPay.role === FamilyUser.MEMBER ? familyPay.memberPaymentInfo : null,
)

export const familyPayPayerPaymentInfoSelector = createSelector(payNextSelector, ({familyPay}) =>
    familyPay.role === FamilyUser.PAYER ? familyPay.payerPaymentInfo : null,
)

export const familyPayPreApprovalPaymentAmountSelector = createSelector(
    familyPayMemberPaymentInfoSelector,
    (memberPaymentInfo) => {
        if (!memberPaymentInfo) {
            return null
        }

        const {limit, used} = memberPaymentInfo

        if (isNil(limit) || isNil(used)) {
            return null
        }

        return {
            /** 월 최대 한도 */
            limit,
            /** 월 사용량 */
            used,
            /** 월 잔여 한도  */
            available: Math.max(limit - used, 0),
        }
    },
)

/**
 * 사전승인결제 1회 한도 selector
 * BE로부터 전달받은 값을 기본적으로 쓰지만 특정 오류로 값이 없는 경우 상수로 들고있는 기본값 사용
 */
export const familyPayPreApprovalPaymentLimitPerTransactionSelector = createSelector(
    familyPaySelector,
    ({preApprovalPaymentLimitPerTransaction}) => preApprovalPaymentLimitPerTransaction,
)
